"use client";

import { useNextRouterContext } from "@faire/retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import {
  AmazonHoverTriangleTrackerDropdownDirection,
  useAmazonHoverTriangleTracker,
} from "@faire/retailer-visitor-shared/components/Header/desktop/NavigationLinksBar/__internal__/AmazonHoverTriangle/useAmazonHoverTriangleTracker";
import { LazyDropdowns } from "@faire/retailer-visitor-shared/components/Header/desktop/NavigationLinksBar/__internal__/Dropdown/LazyDropdowns";
import { INavigationLink } from "@faire/retailer-visitor-shared/components/Header/desktop/NavigationLinksBar/__internal__/INavigationLink";
import { L1Link } from "@faire/retailer-visitor-shared/components/Header/desktop/NavigationLinksBar/__internal__/L1Link";
import { SaleAnnouncementTooltip } from "@faire/retailer-visitor-shared/components/Header/desktop/NavigationLinksBar/__internal__/SaleAnnouncementTooltip";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useMaybeAssignCategoryTreeExperiment } from "@faire/retailer-visitor-shared/lib/useMaybeAssignCategoryTreeExperiment";
import { useSettingDealsHub } from "@faire/retailer-visitor-shared/settings/useSettingDealsHub";
import { useSettingNextJSRscCategoryPage } from "@faire/retailer-visitor-shared/settings/useSettingNextJSRscCategoryPage";
import { useNavigationQuery } from "@faire/retailer-visitor-shared/stores/domain/useNavigationQuery";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { cn } from "@faire/web/tools/cn";
import { useDelayedCallback } from "@faire/web/ui/hooks/useDelayedCallback";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useState } from "@faire/web/ui/hooks/useState";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { trackRetailersTopNavView } from "@faire/web-api--source/events/retailers/view/topNav";
import { IRecordCategoryNodesVisibilityRequest } from "@faire/web-api--source/indigofair/category/IRecordCategoryNodesVisibilityRequest";
import { ClientPlatformType } from "@faire/web-api--source/indigofair/data/ClientPlatformType";
import { INavigationLink as INavigationLinkData } from "@faire/web-api--source/indigofair/data/INavigationLink";
import { IQueryFlashSalesRequest } from "@faire/web-api--source/indigofair/data/IQueryFlashSalesRequest";
import { route as categoryC1Route } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import Source = IRecordCategoryNodesVisibilityRequest.Source;

const HOVER_DELAY = 250;

export const IsUsingTouchGesturesContext = React.createContext(false);

/**
 * The navigation links bar that displays a three-level link hierarchy.
 *
 * L1 links appear in the horizontal navigation bars, and typically represent
 * product categories (but they can also represent non-product categories,
 * such as featured links). Clicking on L1 links opens a dropdown that then
 * shows the child links of the selected L1 link.
 *
 * The dropdown is split into a left and a right column. The left column
 * shows the child links (which can be both L2 and L3) of the selected
 * L1 link. The right column shows the child L3 links of the selected L2 link
 * in the left column.
 *
 * The dropdown is loaded lazily to avoid inflating the initial JS bundle.
 */
export const NavigationLinksBar: React.FC = () => {
  const isSSR = useIsSSR();
  const { isDesktopAndAbove } = useViewport();
  const [isUserInteractingWithTouch, setIsUserInteractingWithTouch] =
    useState<boolean>(false);
  const [resetHoverCounter, setResetHoverCounter] = useState<number>(0);
  const [shouldShowDropdown, setShouldShowDropdown] = useState<boolean>(false);
  const [selectedLinkName, setSelectedLinkName] = useState<string | undefined>(
    undefined
  );
  const [lastFocusedLinkName, setLastFocusedLinkName] = useState<
    string | undefined
  >(undefined);

  const mouseOverLinkName = useRef<string | undefined>(undefined);
  const pointerEventInitiatedWithTouch = useRef<boolean>(false);
  const cancelDropVisibilityTimeoutRef = useRef<
    { cancel: () => void } | undefined
  >(undefined);

  const navBarRef = useRef<HTMLDivElement>(null);
  const linksRef = useRef<HTMLDivElement>(null);
  const { prefetch: nextPrefetch } = useNextRouterContext();

  const { onMouseMove } = useAmazonHoverTriangleTracker(
    linksRef,
    () => setSelectedLinkName(mouseOverLinkName.current),
    AmazonHoverTriangleTrackerDropdownDirection.BOTTOM
  );
  const maybeAssignCategoryTreeExperiment =
    useMaybeAssignCategoryTreeExperiment();

  const { navigationLinkGroup } = useNavigationQuery(
    ClientPlatformType.CLIENT_DESKTOP
  );

  const allDesktopC1Links = useMemo(() => {
    return navigationLinkGroup.flatMap((group) => group.navigation_links);
  }, [navigationLinkGroup]);

  const visibleLinks: INavigationLink[] = useMemo(
    () =>
      allDesktopC1Links
        .filter(
          (link) =>
            link.style_group !== INavigationLinkData.StyleGroup.INVISIBLE &&
            link.style_group !==
              INavigationLinkData.StyleGroup.INVISIBLE_FOR_ASSIGNMENT
        )
        .map(
          ({
            name,
            deep_link_path,
            path_identifier,
            unique_identifier,
            sublinks,
            sale_source,
          }) => ({
            name: name ?? "",
            to: deep_link_path,
            hasSublinks: sublinks.length > 0,
            categoryPathIdentifier: path_identifier,
            uniqueIdentifier: unique_identifier,
            isWarehouseSale:
              sale_source ===
              IQueryFlashSalesRequest.SearchSource.SEASONAL_SALE,
          })
        ),
    [allDesktopC1Links]
  );

  const categoryPathIdentifiersForAssignment = useMemo(() => {
    const invisibleForAssignmentCategoryPathIdentifiers = allDesktopC1Links
      .filter(
        (link) =>
          link.style_group ===
          INavigationLinkData.StyleGroup.INVISIBLE_FOR_ASSIGNMENT
      )
      .map((navigationLink) => navigationLink.path_identifier);

    const visibleCategoryPathIdentifiers = visibleLinks.map(
      (link) => link.categoryPathIdentifier
    );

    return [
      ...invisibleForAssignmentCategoryPathIdentifiers,
      ...visibleCategoryPathIdentifiers,
    ];
  }, [allDesktopC1Links, visibleLinks]);

  useEffect(() => {
    if (isDesktopAndAbove) {
      maybeAssignCategoryTreeExperiment(
        () => categoryPathIdentifiersForAssignment,
        Source.WEB_DESKTOP_TOP_NAV
      );
    }
  }, [
    isDesktopAndAbove,
    categoryPathIdentifiersForAssignment,
    maybeAssignCategoryTreeExperiment,
  ]);

  const delayDropdownVisibility = useDelayedCallback(() =>
    setShouldShowDropdown(true)
  );
  const showDropDown = (delay: number) => {
    cancelDropVisibilityTimeoutRef.current = delayDropdownVisibility(delay);
  };

  // Needs to be wrapped in a useCallback for performance reasons
  const closeOpenDropdown = useCallback(() => {
    setSelectedLinkName(undefined);
    mouseOverLinkName.current = undefined;
  }, []);

  /**
   * Starting interaction with touch. Set flags that the interaction was initiated with touch
   */
  const handleContentTouchStart = () => {
    pointerEventInitiatedWithTouch.current = true;
    setIsUserInteractingWithTouch(true);
  };

  /**
   * **`mouseenter`** will be fired no matter if pointer type is `coarse` or `fine`.
   * - If mouseenter is fired by a touch gesture, it will be fired after `touchstart`.
   *  see [reference](https://developer.mozilla.org/en-US/docs/Web/API/Touch_events/Supporting_both_TouchEvent_and_MouseEvent).
   *
   *  In the touchstart event above, set the pointerEventInitiatedWithTouch flag to true to indicated that the intertaction was
   *  started with touch.
   *
   *  Upon entering mouseEnnter, `wasJustTouched` is set to true in `touchstart`, when we get get, will know that the user is interacting
   *  with the links using touch gestures if and only if `wasJustTouched`=true
   */
  const handleContentMouseEnter = () => {
    let localIsUserInteractingWithTouch = isUserInteractingWithTouch;
    if (!pointerEventInitiatedWithTouch.current && isUserInteractingWithTouch) {
      // Event wasn't initiated by touch, but isUserInteractingWithTouch is still true
      // Need to reset it to false for the downstream event handlers
      localIsUserInteractingWithTouch = false;
      setIsUserInteractingWithTouch(localIsUserInteractingWithTouch);
    }

    if (!shouldShowDropdown && !localIsUserInteractingWithTouch) {
      // Mouse entered the links bar, so set dropdown to show after a delay
      showDropDown(HOVER_DELAY);
    }

    // Reset pointerEventInitiatedWithTouch as it isn't needed for the remainder of the event chain
    pointerEventInitiatedWithTouch.current = false;
  };

  const isLoggedInRetailer = useIsLoggedInRetailer();
  const isRSCCategoryPageEnabled = useSettingNextJSRscCategoryPage();

  /**
   * This event will be fired after content mouse enter (on both touch and mouse events)
   * If interacting through touch, the selected link will be handled in the click event
   *
   * If interacting through a mouse, these represent a mouse over the nav link
   */
  const handleMouseEnterLink = (link: INavigationLink) => {
    if (!isUserInteractingWithTouch && lastFocusedLinkName !== link.name) {
      mouseOverLinkName.current = link.name;
      // Mouse hovered the
      if (!selectedLinkName) {
        if (isLoggedInRetailer && !isRSCCategoryPageEnabled) {
          nextPrefetch(categoryC1Route);
        }
        setSelectedLinkName(link.name);
      }
      setLastFocusedLinkName(link.name);
    }
  };

  const handleClickLink = (link: INavigationLink) => {
    if (isUserInteractingWithTouch) {
      // This click was initiated through a touch event
      // - If user tapped the already selected link name, assume they want to toggle to close the dropdown
      // - If user tapped a different item, we assume they want to switch to that link.
      if (selectedLinkName === link.name) {
        setShouldShowDropdown(false);
        closeOpenDropdown();
      } else {
        if (isLoggedInRetailer && !isRSCCategoryPageEnabled) {
          nextPrefetch(categoryC1Route);
        }
        mouseOverLinkName.current = link.name;
        setSelectedLinkName(link.name);
        showDropDown(0);
      }
    } else {
      // The click was initiated through the mouse, Bump the key to refresh the hover behaviour.
      setResetHoverCounter(resetHoverCounter + 1);
      closeOpenDropdown();
    }
  };

  const handleDropdownClick = () => {
    setLastFocusedLinkName(undefined);
    closeOpenDropdown();
  };

  const handleContentMouseLeave = () => {
    if (!isUserInteractingWithTouch) {
      // Mouse left the links bar, so cancel the dropdown show
      setSelectedLinkName(undefined);
      setLastFocusedLinkName(undefined);
      mouseOverLinkName.current = undefined;
      setShouldShowDropdown(false);
    }

    cancelDropVisibilityTimeoutRef.current?.cancel();
  };

  const handleNavLinksBarVisible = useDoActionWhenVisible(
    trackRetailersTopNavView
  );

  return (
    <IsUsingTouchGesturesContext.Provider value={isUserInteractingWithTouch}>
      <div
        ref={handleNavLinksBarVisible}
        className="bg-core-background-primary flex min-h-[47px] w-full flex-row justify-center"
      >
        {isSSR ? null : (
          <div
            ref={navBarRef}
            key={resetHoverCounter}
            onMouseEnter={handleContentMouseEnter}
            onFocus={handleContentMouseEnter}
            onMouseLeave={handleContentMouseLeave}
            onBlur={handleContentMouseLeave}
            onTouchStart={handleContentTouchStart}
            className="bg-core-surface-inverse relative flex"
          >
            <div
              onMouseMove={onMouseMove}
              data-test-id="desktop-header-c1-categories"
              className="relative flex flex-row flex-wrap justify-center px-[24px] pt-[10px] pb-[8px]"
            >
              {visibleLinks?.map((link) => (
                <L1Link
                  key={link.name}
                  link={link}
                  selectedLinkName={selectedLinkName}
                  onClick={handleClickLink}
                  onMouseEnter={handleMouseEnterLink}
                  className={cn("relative", "px-[7px]", "py-[4px]")}
                  tooltip={<TooltipRenderer categoryLink={link} />}
                  isUserInteractingWithTouch={isUserInteractingWithTouch}
                />
              ))}
              <div
                ref={linksRef}
                className="invisible absolute bottom-0 w-full"
              />
            </div>
            <Suspense fallback={null}>
              <LazyDropdowns
                selectedLinkName={
                  shouldShowDropdown ? selectedLinkName : undefined
                }
                onClick={handleDropdownClick}
              />
            </Suspense>
          </div>
        )}
      </div>
    </IsUsingTouchGesturesContext.Provider>
  );
};

const TooltipRenderer: React.FC<{ categoryLink: INavigationLink }> = ({
  categoryLink,
}) => {
  const isSaleHubUpdate = useSettingDealsHub();

  if (categoryLink.categoryPathIdentifier === "sale" && isSaleHubUpdate) {
    return <SaleAnnouncementTooltip />;
  }

  return null;
};
